import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Download, AlertCircle, RefreshCw, ChevronUp } from "lucide-react";
import "./Report.print.css";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ExecutiveSummaryCompact } from "@/components/report/ExecutiveSummaryCompact";
import { DecisionSnapshot } from "@/components/report/DecisionSnapshot";
import { ProblemCoreSection } from "@/components/report/ProblemCoreSection";
import { OptionsCompact } from "@/components/report/OptionsCompact";
import { EvaluationMatrix } from "@/components/report/EvaluationMatrix";
import { DominantStrategySection } from "@/components/report/DominantStrategySection";
import { CommitmentBlock } from "@/components/report/CommitmentBlock";
import { ActionPlanSection } from "@/components/report/ActionPlanSection";
import { GoDecisionBlock } from "@/components/report/GoDecisionBlock";
import { ReportPDFPreview } from "@/components/report/ReportPDFPreview";
import { ReportLoadingScreen } from "@/components/report/ReportLoadingScreen";
import { ReportNavigation } from "@/components/report/ReportNavigation";
import { PhaseNavigationDialog } from "@/components/session/PhaseNavigationDialog";
import { GlobalPhaseTimeline } from "@/components/session/GlobalPhaseTimeline";
import { StrategicPathSection } from "@/components/report/StrategicPathSection";
import { SuccessCriteriaSection } from "@/components/report/SuccessCriteriaSection";
import { ConflictSection } from "@/components/report/ConflictSection";
import quentariLogo from "@/assets/quentari-logo.png";

export default function Report() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [report, setReport] = useState<any>(null);
  const [branding, setBranding] = useState<any>(null);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [sessionMeta, setSessionMeta] = useState<any>(null); // NEU: Session-Meta für Commitment
  const [showPreview, setShowPreview] = useState(false);
  const [isOutdated, setIsOutdated] = useState(false);
  const [lastSessionUpdate, setLastSessionUpdate] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showBackDialog, setShowBackDialog] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("executive-summary");
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Handle scroll to update active section and show scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
      
      // Update active section based on scroll position
      const sections = ["executive-summary", "decision-snapshot", "success-criteria", "conflict-section", "options", "matrix", "dominant-strategy", "strategic-path", "commitment", "action-plan", "go-decision"];
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (sessionId) {
      loadReport();
    }
  }, [sessionId]);

  const checkIfReportIsOutdated = async (reportUpdatedAt: string) => {
    try {
      const { data: sessionData } = await supabase
        .from("decision_sessions")
        .select("updated_at, workspace_id")
        .eq("id", sessionId)
        .single();

      if (!sessionData) return false;

      const sessionUpdate = new Date(sessionData.updated_at).getTime();
      const reportUpdate = new Date(reportUpdatedAt).getTime();
      
      const { data: scenarios } = await supabase
        .from("scenarios")
        .select("updated_at")
        .eq("session_id", sessionId)
        .order("updated_at", { ascending: false })
        .limit(1);

      const scenarioUpdate = scenarios?.[0] 
        ? new Date(scenarios[0].updated_at).getTime() 
        : 0;

      const { data: matrixEntries } = await supabase
        .from("decision_matrix_entries")
        .select("updated_at")
        .eq("session_id", sessionId)
        .order("updated_at", { ascending: false })
        .limit(1);

      const matrixUpdate = matrixEntries?.[0]
        ? new Date(matrixEntries[0].updated_at).getTime()
        : 0;

      const latestUpdate = Math.max(sessionUpdate, scenarioUpdate, matrixUpdate);
      const outdated = latestUpdate > reportUpdate;
      setIsOutdated(outdated);
      
      if (outdated) {
        setLastSessionUpdate(new Date(latestUpdate).toISOString());
      }

      return outdated;
    } catch (error) {
      console.error("Error checking report freshness:", error);
      return false;
    }
  };

  // State for live data from DB
  const [liveOptions, setLiveOptions] = useState<any[]>([]);
  const [liveMatrix, setLiveMatrix] = useState<any[]>([]);

  const loadReport = async () => {
    try {
      setIsLoading(true);
      
      // Load report AND live data in parallel
      const [reportResult, sessionResult, optionsResult, matrixResult] = await Promise.all([
        supabase
          .from("decision_reports")
          .select("*")
          .eq("session_id", sessionId)
          .maybeSingle(),
        supabase
          .from("decision_sessions")
          .select("workspace_id, meta")
          .eq("id", sessionId)
          .single(),
        // CRITICAL: Load live options from DB
        supabase
          .from("scenarios")
          .select("id, name, description, archetype, strategic_goal, key_advantages, key_risks, fits_if, does_not_fit_if, scenario_type, order_index")
          .eq("session_id", sessionId)
          .neq("scenario_type", "baseline")
          .order("order_index"),
        // CRITICAL: Load live matrix from DB
        supabase
          .from("decision_matrix_entries")
          .select("*")
          .eq("session_id", sessionId)
      ]);

      if (reportResult.error && reportResult.error.code !== "PGRST116") throw reportResult.error;

      // Store live data
      if (optionsResult.data) {
        setLiveOptions(optionsResult.data);
      }
      if (matrixResult.data) {
        setLiveMatrix(matrixResult.data);
      }

      if (!reportResult.data) {
        await generateReport();
      } else {
        setReport(reportResult.data);
        await checkIfReportIsOutdated(reportResult.data.updated_at);
      }

      if (sessionResult.data) {
        setWorkspaceId(sessionResult.data.workspace_id);
        setSessionMeta(sessionResult.data.meta);
        
        const { data: workspaceData } = await supabase
          .from("workspaces")
          .select("branding_settings")
          .eq("id", sessionResult.data.workspace_id)
          .single();

        if (workspaceData?.branding_settings) {
          setBranding(workspaceData.branding_settings);
        }
      }
    } catch (error) {
      console.error("Error loading report:", error);
      toast({
        title: "Fehler",
        description: "Der Report konnte nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateMatrixCompleteness = async (): Promise<boolean> => {
    try {
      const { data: options, error: optionsError } = await supabase
        .from("scenarios")
        .select("id, name, scenario_type")
        .eq("session_id", sessionId);

      if (optionsError) throw optionsError;
      if (!options || options.length === 0) {
        setValidationErrors(["Keine Optionen vorhanden. Bitte erstellen Sie zunächst Optionen in Phase 2."]);
        return false;
      }

      // Filter out baseline - it's optional for report generation
      const nonBaselineOptions = options.filter(o => o.scenario_type !== 'baseline');
      
      if (nonBaselineOptions.length === 0) {
        setValidationErrors(["Keine strategischen Optionen vorhanden. Bitte erstellen Sie zunächst Optionen in Phase 2."]);
        return false;
      }

      const { data: matrixEntries, error: matrixError } = await supabase
        .from("decision_matrix_entries")
        .select("option_id, criterion, score, weight")
        .eq("session_id", sessionId);

      if (matrixError) throw matrixError;

      const errors: string[] = [];

      if (!matrixEntries || matrixEntries.length === 0) {
        errors.push("Die Decision Matrix ist leer. Bitte bewerten Sie die Optionen in Phase 3.");
        setValidationErrors(errors);
        return false;
      }

      // Extract unique criteria from the actual matrix entries (they are dynamically generated per session)
      const allCriteria = [...new Set(matrixEntries.map(e => e.criterion))];
      
      // Only validate non-baseline options (baseline is optional)
      nonBaselineOptions.forEach(option => {
        const optionEntries = matrixEntries.filter(e => e.option_id === option.id);
        const optionCriteria = optionEntries.map(e => e.criterion);
        
        // Check that this option has entries for all criteria present in the session
        allCriteria.forEach(criterion => {
          if (!optionCriteria.includes(criterion)) {
            errors.push(`Option "${option.name}": Fehlende Bewertung für Kriterium "${criterion}"`);
          }
        });

        // Validate that all entries have scores
        optionEntries.forEach(entry => {
          if (!entry.score || entry.score.trim() === "") {
            errors.push(`Option "${option.name}": Kein Score für Kriterium "${entry.criterion}"`);
          }
        });
      });

      console.log("Matrix validation:", { options: nonBaselineOptions.length, entries: matrixEntries.length, errors });
      setValidationErrors(errors);
      return errors.length === 0;
    } catch (error) {
      console.error("Error validating matrix:", error);
      setValidationErrors(["Fehler bei der Validierung der Matrix-Einträge."]);
      return false;
    }
  };

  const generateReport = async () => {
    const isValid = await validateMatrixCompleteness();
    
    if (!isValid) {
      console.warn("Matrix validation failed:", validationErrors);
      toast({
        title: "Unvollständige Daten",
        description: "Bitte vervollständigen Sie die Decision Matrix, bevor Sie den Report generieren.",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    setValidationErrors([]);
    try {
      console.log("Generating report for session:", sessionId);
      const { data, error } = await supabase.functions.invoke("phase3-report", {
        body: { session_id: sessionId },
      });

      console.log("Report generation response:", { data, error });

      if (error) throw error;

      if (data?.report) {
        setReport(data.report);
        setIsOutdated(false);
        toast({
          title: "Report generiert",
          description: "Der Entscheidungsreport wurde erfolgreich erstellt.",
        });
        await loadReport();
      } else {
        console.error("No report in response:", data);
        toast({
          title: "Fehler",
          description: "Der Report wurde nicht korrekt generiert.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Error generating report:", error);
      toast({
        title: "Fehler",
        description: error.message || "Der Report konnte nicht generiert werden.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const [targetPhase, setTargetPhase] = useState<number | null>(null);

  const handlePhaseNavigation = (phase: number) => {
    setTargetPhase(phase);
    setShowBackDialog(true);
  };

  const handleConfirmNavigation = async () => {
    if (!sessionId || targetPhase === null) return;
    
    try {
      if (targetPhase === 1) {
        await supabase
          .from("decision_sessions")
          .update({ phase: 1, subphase: "1.4" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}`);
      } else if (targetPhase === 2) {
        await supabase
          .from("decision_sessions")
          .update({ phase: 2, subphase: "2.0" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}/phase2`);
      } else if (targetPhase === 3) {
        await supabase
          .from("decision_sessions")
          .update({ phase: 3, subphase: "3.3" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}/phase3`);
      }
    } catch (error: any) {
      console.error("Error navigating to phase:", error);
      toast({
        title: "Fehler",
        description: "Navigation fehlgeschlagen.",
        variant: "destructive",
      });
    }
  };

  const getPhaseLabel = (phase: number) => {
    const labels: Record<number, string> = {
      1: "Phase 1 – Analyse",
      2: "Phase 2 – Optionen",
      3: "Phase 3 – Bewertung",
      4: "Report"
    };
    return labels[phase] || `Phase ${phase}`;
  };

  if (isLoading || isGenerating) {
    return <ReportLoadingScreen isGenerating={isGenerating} />;
  }

  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center space-y-4 max-w-md">
          <p className="text-muted-foreground">
            Kein Report verfügbar. Bitte generiere zuerst einen Report.
          </p>
          <Button onClick={generateReport}>Report generieren</Button>
        </div>
      </div>
    );
  }

  // Extract recommended option label - prioritize from recommendation
  const recommendedOptionLabel = report.recommendation?.recommended_option_label || 
    report.executive_summary?.recommended_option_label || "";

  // CRITICAL: Build options list from LIVE DB data, fallback to report
  const optionsList = liveOptions.length > 0 
    ? liveOptions.map((opt: any) => ({
        option_id: opt.id,
        label: opt.name
      }))
    : (report.options_overview?.options?.map((opt: any) => ({
        option_id: opt.option_id,
        label: opt.label
      })) || []);

  // CRITICAL: Build live options_overview data from DB - NEVER override with stored report data
  const liveOptionsOverview = liveOptions.length > 0 
    ? {
        title: report.options_overview?.title || "Strategische Optionen",
        options: liveOptions.map((opt: any) => {
          // Find AI enrichment from report (only for non-identifying fields)
          const reportOpt = report.options_overview?.options?.find((ro: any) => ro.option_id === opt.id);
          return {
            option_id: opt.id,
            label: opt.name, // ALWAYS from DB
            archetype: opt.archetype, // ALWAYS from DB
            strategic_goal: opt.strategic_goal || opt.description, // ALWAYS from DB
            key_advantages: opt.key_advantages || [], // ALWAYS from DB
            key_risks: opt.key_risks || [], // ALWAYS from DB
            fits_if: opt.fits_if || [],
            does_not_fit_if: opt.does_not_fit_if || [],
            // Only merge AI-enrichment fields that don't override identity
            why_fits: reportOpt?.why_fits,
            when_this_option_fits: reportOpt?.when_this_option_fits,
            hypothesis_link: reportOpt?.hypothesis_link,
          };
        })
      }
    : report.options_overview;

  // CRITICAL: Build live matrix data from DB - match Phase 3 UI scoring (3/2/1)
  const buildLiveMatrixData = () => {
    if (liveMatrix.length === 0) return report.comparison_matrix_summary;

    // Use phase3_criteria from session meta for correct weights and labels
    const phase3Criteria = sessionMeta?.phase3_criteria || [];
    
    // Get unique criteria from matrix entries if no phase3_criteria
    const criteriaFromEntries = [...new Set(liveMatrix.map((e: any) => e.criterion))];
    const allCriteria = phase3Criteria.length > 0 
      ? phase3Criteria.map((c: any) => c.key || c.criterion)
      : criteriaFromEntries;
    
    // Calculate equal weights like Phase 3 UI
    const criteriaCount = allCriteria.length;
    const baseWeight = criteriaCount > 0 ? Math.round(100 / criteriaCount) : 25;
    
    // Build criteria label map from phase3_criteria
    const criteriaLabelMap: Record<string, string> = {};
    phase3Criteria.forEach((c: any) => {
      criteriaLabelMap[c.key || c.criterion] = c.label || c.key || c.criterion;
    });

    // Build matrix view per criterion
    const matrix_view = allCriteria.map((criterion: string, index: number) => {
      // First criterion gets remainder to ensure sum is exactly 100
      const weight = index === 0 && criteriaCount > 0 
        ? 100 - (baseWeight * (criteriaCount - 1))
        : baseWeight;
      
      const criterionEntries = liveMatrix.filter((e: any) => e.criterion === criterion);
      
      // CRITICAL: Use 3/2/1 scale like Phase 3 UI
      const scores = criterionEntries.map((entry: any) => {
        const scoreValue = entry.score?.toLowerCase();
        const scoreNumeric = (scoreValue === 'high' || scoreValue === 'hoch') ? 3 :
          (scoreValue === 'medium' || scoreValue === 'mittel') ? 2 : 1;
        return {
          option_id: entry.option_id,
          score: entry.score || "medium",
          points: scoreNumeric,
          weighted_points: (scoreNumeric * weight) / 100
        };
      });

      return { 
        criterion, 
        label: criteriaLabelMap[criterion] || criterion,
        weight, 
        scores 
      };
    });

    // Calculate totals using 3/2/1 scale
    const optionIds = [...new Set(liveMatrix.map((e: any) => e.option_id))];
    const option_totals = optionIds.map(optionId => {
      const optionEntries = liveMatrix.filter((e: any) => e.option_id === optionId);
      let totalPoints = 0;
      let weightedTotal = 0;

      optionEntries.forEach((entry: any) => {
        const scoreValue = entry.score?.toLowerCase();
        const points = (scoreValue === 'high' || scoreValue === 'hoch') ? 3 :
          (scoreValue === 'medium' || scoreValue === 'mittel') ? 2 : 1;
        const criterionIndex = allCriteria.indexOf(entry.criterion);
        const weight = criterionIndex === 0 && criteriaCount > 0 
          ? 100 - (baseWeight * (criteriaCount - 1))
          : baseWeight;
        totalPoints += points;
        weightedTotal += (points * weight) / 100;
      });

      // Find label from live options
      const optionLabel = liveOptions.find((o: any) => o.id === optionId)?.name || "Option";

      return {
        option_id: optionId,
        label: optionLabel,
        total_points: totalPoints,
        weighted_total: weightedTotal,
        max_points: criteriaCount * 3 // Max is 3 per criterion
      };
    });

    return {
      ...report.comparison_matrix_summary,
      matrix_view,
      option_totals,
      criteria_used: allCriteria,
      criteria_label_map: criteriaLabelMap,
      weighted: true,
      source: "phase3",
      scale: 3 // Indicate 1-3 scale
    };
  };

  const liveMatrixData = buildLiveMatrixData();

  // Extract failure analysis from report (check metadata for v7 structure)
  const failureAnalysis = report.failure_analysis || report.metadata?.failure_analysis || {
    failure_indicators: [],
    pivot_triggers: [],
  };

  // Extract go_decision from metadata if not at top level
  const goDecision = report.go_decision || report.metadata?.go_decision;


  // Extract structured success criteria from session meta (Phase 1.1)
  const successCriteria = {
    signal: sessionMeta?.success_signals?.[0] || report.metadata?.success_signals?.[0],
    timeframe: sessionMeta?.success_timeframe || report.metadata?.success_timeframe,
    validators: sessionMeta?.success_validators || report.metadata?.success_validators || [],
    notes: sessionMeta?.success_notes || report.metadata?.success_notes,
  };

  // Extract Phase 1.2 conflict data from session meta
  const conflictData = {
    strategyA: sessionMeta?.conflict_strategy_a || report.metadata?.conflict_strategy_a,
    strategyB: sessionMeta?.conflict_strategy_b || report.metadata?.conflict_strategy_b,
    costs: sessionMeta?.conflict_costs || report.metadata?.conflict_costs,
  };

  // Extract primary hypothesis for options display
  const primaryHypothesis = report.problem_and_context?.primary_hypothesis;

  // Build commitment data from session meta (DecisionCommitDialog speichert in session.meta.decision_commit)
  // CRITICAL: Use decision_commit, not commitment
  const sessionCommitment = sessionMeta?.decision_commit || {};
  const reportGoDecision = report.metadata?.go_decision || {};
  
  const commitmentData = {
    recommended_option_id: sessionCommitment.committed_option_id || report.recommendation?.recommended_option_id,
    recommended_option_label: sessionCommitment.committed_option_name || recommendedOptionLabel,
    validity_days: sessionCommitment.validity_days || reportGoDecision.validity_days || 30,
    owner: sessionCommitment.owner_name || sessionCommitment.owner || reportGoDecision.owner,
    review_date: sessionCommitment.review_date || reportGoDecision.review_date,
    committed_at: sessionCommitment.committed_at || reportGoDecision.committed_at,
    excluded_options: sessionCommitment.excluded_options,
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b print-hide no-print">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            <img 
              src={quentariLogo} 
              alt="Quentari" 
              className="h-8 w-auto cursor-pointer transition-transform duration-300 hover:scale-105" 
              onClick={() => navigate('/dashboard')}
            />
            <div className="h-6 w-px bg-border hidden sm:block" />
            <GlobalPhaseTimeline
              currentPhase={4}
              sessionId={sessionId!}
              onPhaseClick={handlePhaseNavigation}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={generateReport}
              className="text-muted-foreground"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Neu generieren</span>
            </Button>
            <Button onClick={() => setShowPreview(true)}>
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Section Navigation (Desktop) */}
      <ReportNavigation 
        activeSection={activeSection} 
        onNavigate={navigateToSection} 
      />

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-40 p-3 bg-primary text-primary-foreground rounded-full shadow-lg hover:bg-primary/90 transition-all print-hide no-print"
          aria-label="Scroll to top"
        >
          <ChevronUp className="h-5 w-5" />
        </button>
      )}

      {/* Report Content - Single Linear Page */}
      <div className="container mx-auto px-4 py-8 max-w-4xl space-y-8">
        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Matrix-Validierung fehlgeschlagen</AlertTitle>
            <AlertDescription>
              <ul className="list-disc list-inside space-y-1 mt-2">
                {validationErrors.map((error, index) => (
                  <li key={index} className="text-sm">{error}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Outdated Warning */}
        {isOutdated && (
          <Alert variant="default" className="border-warning bg-warning/10">
            <AlertCircle className="h-4 w-4 text-warning" />
            <AlertTitle>Report ist veraltet</AlertTitle>
            <AlertDescription className="flex items-center justify-between">
              <span>
                Die Daten wurden seit der Generierung aktualisiert
                {lastSessionUpdate && (
                  <> (zuletzt am {new Date(lastSessionUpdate).toLocaleString("de-DE")})</>
                )}.
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={generateReport}
                disabled={isGenerating}
                className="ml-4 shrink-0"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Aktualisieren
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* 4.1 Executive Summary */}
        <ExecutiveSummaryCompact 
          data={report.executive_summary}
          problemStatement={report.problem_and_context?.problem_statement}
          recommendedOption={recommendedOptionLabel}
        />

        {/* 4.2 Decision Snapshot - NEW: Eigenständiger Block */}
        <DecisionSnapshot 
          data={report.problem_and_context}
        />

        {/* 4.2b Erfolgskriterien - Strukturierte Phase 1.1 Daten */}
        <SuccessCriteriaSection
          successSignal={successCriteria.signal}
          timeframe={successCriteria.timeframe}
          validators={successCriteria.validators}
          additionalNotes={successCriteria.notes}
        />

        {/* 4.2c Zielkonflikt - Strukturierte Phase 1.2 Daten */}
        <ConflictSection
          strategyA={conflictData.strategyA}
          strategyB={conflictData.strategyB}
          costs={conflictData.costs}
        />

        {/* 4.3 Relevant Options with Hypothesis Link - LIVE DATA */}
        <OptionsCompact 
          data={liveOptionsOverview}
          primaryHypothesis={primaryHypothesis}
        />

        {/* 4.4 Evaluation Matrix - LIVE DATA from Phase 3 */}
        <EvaluationMatrix 
          data={liveMatrixData}
          options={optionsList}
          recommendedOptionId={report.recommendation?.recommended_option_id}
        />

        {/* 4.5 Dominant Strategy */}
        <DominantStrategySection data={report.recommendation} />

        {/* 4.5b Strategic Transition Path */}
        <StrategicPathSection 
          data={report.strategic_path || report.metadata?.strategic_path}
          alternativeOptions={liveOptionsOverview?.options?.filter(
            (opt: any) => opt.option_id !== report.recommendation?.recommended_option_id
          )}
        />

        {/* 4.6 Verbindlicher Commit - NEW */}
        <CommitmentBlock 
          data={commitmentData}
          recommendedOption={recommendedOptionLabel}
          options={optionsList}
        />

        {/* 4.7 Action Plan */}
        <ActionPlanSection 
          data={report.action_roadmap}
          failureAnalysis={report.failure_analysis}
          sessionId={sessionId}
          workspaceId={workspaceId || undefined}
        />

        {/* 4.8 Go Decision Block */}
        <GoDecisionBlock 
          data={report.go_decision}
          recommendedOption={recommendedOptionLabel}
        />
      </div>

      {/* PDF Preview Dialog - with corrected live data */}
      <ReportPDFPreview
        report={{
          ...report,
          options_overview: liveOptionsOverview,
          comparison_matrix_summary: liveMatrixData
        }}
        branding={branding}
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        sessionId={sessionId || ""}
        sessionMeta={sessionMeta}
      />

      {/* Phase Navigation Dialog */}
      <PhaseNavigationDialog
        open={showBackDialog}
        onOpenChange={setShowBackDialog}
        onConfirm={handleConfirmNavigation}
        fromPhase="Report"
        toPhase={getPhaseLabel(targetPhase || 3)}
      />
    </div>
  );
}
